import * as React from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/auth/useAuth";

export function SignInPage() {
  const auth = useAuth();
  const [mode, setMode] = React.useState<"sign-in" | "sign-up">("sign-in");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);

  if (auth.ready && auth.user) {
    return <Navigate to="/" replace />;
  }

  const signingUp = mode === "sign-up";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (signingUp && auth.signUp) await auth.signUp(email, password, name);
      else await auth.signIn(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed. Check your credentials.");
    } finally {
      setPending(false);
    }
  }

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-bg p-6"
      aria-labelledby="signin-title"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle id="signin-title">{signingUp ? "Create your Omni account" : "Sign in to Omni"}</CardTitle>
          <CardDescription>One brain. Every device. Always on.</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Email / password */}
          {auth.error ? (
            <p role="alert" className="mb-4 text-xs text-danger">
              {auth.error}
            </p>
          ) : null}
          <form className="space-y-4" onSubmit={onSubmit}>
            {signingUp ? (
              <label className="block space-y-1">
                <span className="text-xs font-medium text-fg-muted">Name</span>
                <Input
                  name="name"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
            ) : null}
            <label className="block space-y-1">
              <span className="text-xs font-medium text-fg-muted">Email</span>
              <Input
                type="email"
                name="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-fg-muted">Password</span>
              <Input
                type="password"
                name="password"
                autoComplete={signingUp ? "new-password" : "current-password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            {error ? (
              <p role="alert" className="text-xs text-danger">
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" size="lg" disabled={pending || !auth.ready}>
              {pending
                ? signingUp
                  ? "Creating account…"
                  : "Signing in…"
                : signingUp
                  ? "Create account"
                  : "Sign in"}
            </Button>
          </form>

          {auth.canSignUp ? (
            <button
              type="button"
              className="mt-4 w-full text-xs text-fg-muted underline-offset-2 hover:underline"
              onClick={() => {
                setError(null);
                setMode(signingUp ? "sign-in" : "sign-up");
              }}
            >
              {signingUp ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>
          ) : null}

        </CardContent>
      </Card>
    </main>
  );
}
